import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSelector, useStore } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../theme';
import TicketingAnalyticsFiltersPanel from '../../components/TicketingAnalyticsFiltersPanel';
import TicketingAnalyticsPlaceholderTab from '../../components/TicketingAnalyticsPlaceholderTab';
import TicketingAnalyticsTabs from '../../components/TicketingAnalyticsTabs';
import TicketingAnalyticsSummaryRow from '../../components/TicketingAnalyticsSummaryRow';
import { getTicketingSummary } from './ticketingAnalyticsSummary';
import { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import { FontFamily } from '../../../../constants/fonts';
import { URGENCY_BG } from '../../../../constants/ticketStatusConstants';
import { locationApi } from '../../../Admin/pages/LocationManagement/locationApi';
import { useAdminLocations } from '../../../AnalyticsHub/hooks/useAdminLocations';
import { createApiClient } from '../../../../api/apiClient';
import { ticketingConfigApi } from '../../../../api/ticketingConfigApi';
import {
  selectCategoryBetaParams,
  selectCategoryContextKey,
  selectIsCategoryConfigStale,
} from '../../../../store/selectors/appDataSelectors';
import useTicketingAccess from '../../../TicketingPortal/hooks/useTicketingAccess';
import {
  selectDistinctRegions,
  selectDistinctMarkets,
  selectDistinctLocationNames,
  selectDistinctTicketingCategories,
  selectTicketingSubCategoriesWithCategory,
  getTicketingResolutionPathItems,
  getTicketingStatusItems,
} from '../../../../store/selectors/appDataSelectors';
import { RootState, useAppDispatch } from '../../../../store';
import {
  buildTicketingAnalyticsFiltersPayload,
  buildTicketingTimePeriodFilter,
  resetTicketingAnalyticsFiltersPayload,
  setTicketingAnalyticsFiltersPayload,
} from '../../../../store/slices/ticketingAnalyticsFiltersSlice';
import { setSubmitTicketFormConfigSuccess } from '../../../../store/slices/ticketingPortalSlice';
import { fetchSustainTeamMembers } from '../../../../store/thunks/ticketingPortalThunk';
import {
  getCategoryBetaKey,
  getCategoryBetaParams,
  isCategoryConfigStale,
} from '../../../../utils/ticketingCategoryBeta';
import TeamSelectorDropdown from './TeamSelectorDropdown';
import Overview from './Overview';
import VolumeAndTrends from './VolumeAndTrends';
import Lifecycle from './Lifecycle';
import TeamWorkload from './TeamWorkload';
import Categories from './Categories';
import SubmittersLocations from './SubmittersLocations';
import Rca from './Rca';
import {
  exportTicketsData,
  fetchNavigatorTicketingCategories,
  fetchNavigatorTicketingLifecycle,
  fetchNavigatorTicketingSubmittersLocations,
  fetchNavigatorTicketingTeamWorkload,
  fetchNavigatorTicketingVolumeTrends,
  fetchNavigatorTicketingRca,
  fetchTicketingAssignedMembers,
  fetchTicketingStatusAvgTimeByCategory,
  fetchTicketingOverviewCoreMetrics,
  fetchTicketingOverviewStatusBreakdown,
  fetchTicketingOverviewAgingTickets,
  fetchTicketingOverviewCategoryBreakdown,
  fetchTicketingOverviewDuplicateActivity,
  fetchTicketingLifecycleOverview,
  fetchTicketingLifecycleResolvingParty,
  fetchTicketingLifecycleStaleTickets,
  fetchTicketingLifecycleResolutionTimePerCategory,
  type CategoryLookup,
} from './ticketingAnalyticsKpiApi';
import { useAsyncSection } from '../../hooks/useAsyncSection';
import type {
  TicketingAnalyticsCustomDateRange,
  TicketingAnalyticsSelectedFilters,
  TicketingAnalyticsTab,
  TicketingAnalyticsTeamOption,
  TicketingAnalyticsTeamScope,
  TicketingTabItem,
  LifecycleStatusData,
  LifecycleStatusViewOption,
  TimePeriod,
  TimePeriodTab,
  PriorityView,
} from '../../components/types';
import type { TicketingAnalyticsFilterKey } from '../../components/types';

interface TicketingAnalyticsDashboardProps {
  onBack: () => void;
}

/** Loading/error pair for a single tab's data request. */
interface TicketingTabRequestState {
  loading: boolean;
  error: string | null;
}

const TIME_PERIOD_TABS: TimePeriodTab[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'period', label: 'Period' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
  { key: 'custom', label: 'Custom Range' },
];

const TABS: TicketingTabItem[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'volume-trends', label: 'Volume and Trends' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'team-workload', label: 'Team Workload' },
  { key: 'categories', label: 'Categories' },
  { key: 'submitters-locations', label: 'Submitters and Locations' },
  { key: 'rca', label: 'RCA' },
];

/** Fallback chip shown before the category config loads. */
const LIFECYCLE_STATUS_VIEW_ALL_OPTION: LifecycleStatusViewOption = { key: 'all', label: 'All' };

const ALL_OPTION: KPIFilterDropdownItem = { id: 'all', displayText: 'All' };

const createEmptySelection = (): TicketingAnalyticsSelectedFilters => ({
  regions: [],
  markets: [],
  locations: [],
  categories: [],
  subCategories: [],
  tertiaries: [],
  statuses: [],
  resolutionPaths: [],
  urgencies: [],
  assignedTo: [],
});

const createEmptyCustomDateRange = (): TicketingAnalyticsCustomDateRange => ({
  startDate: null,
  endDate: null,
});

const URGENCY_FILTER_ITEMS: KPIFilterDropdownItem[] = Object.keys(URGENCY_BG).map((urgency) => ({
  id: urgency,
  displayText: urgency.charAt(0).toUpperCase() + urgency.slice(1),
}));

const cloneCustomDateRange = (range: TicketingAnalyticsCustomDateRange): TicketingAnalyticsCustomDateRange => ({
  startDate: range.startDate,
  endDate: range.endDate,
});

const cloneFilters = (filters: TicketingAnalyticsSelectedFilters): TicketingAnalyticsSelectedFilters => ({
  regions: [...filters.regions],
  markets: [...filters.markets],
  locations: [...filters.locations],
  categories: [...filters.categories],
  subCategories: [...filters.subCategories],
  tertiaries: [...(filters.tertiaries ?? [])],
  statuses: [...filters.statuses],
  resolutionPaths: [...filters.resolutionPaths],
  urgencies: [...filters.urgencies],
  assignedTo: [...filters.assignedTo],
});

const withAllOption = (options: KPIFilterDropdownItem[]): KPIFilterDropdownItem[] => {
  if (!options.length) {
    return [ALL_OPTION];
  }

  return options;
};

const selectionsEqual = (left: KPIFilterDropdownItem[], right: KPIFilterDropdownItem[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const leftIds = left.map((item) => String(item.id)).sort();
  const rightIds = right.map((item) => String(item.id)).sort();
  return leftIds.every((id, index) => id === rightIds[index]);
};

const customDateRangesEqual = (
  left: TicketingAnalyticsCustomDateRange,
  right: TicketingAnalyticsCustomDateRange,
): boolean => {
  return left.startDate === right.startDate && left.endDate === right.endDate;
};

const placeholderCopy: Record<Exclude<TicketingAnalyticsTab, 'overview'>, { icon: string; title: string; description: string }> = {
  'volume-trends': {
    icon: 'show-chart',
    title: 'Volume and Trends',
    description: 'This tab is the next migration target and still uses the legacy Ticketing Analytics implementation.',
  },
  lifecycle: {
    icon: 'device-hub',
    title: 'Lifecycle',
    description: 'Lifecycle will be moved into NavigatorReports after the Overview slice is validated.',
  },
  'team-workload': {
    icon: 'groups',
    title: 'Team Workload',
    description: 'Team workload is still pending migration into the new NavigatorReports page structure.',
  },
  categories: {
    icon: 'category',
    title: 'Categories',
    description: 'Categories remains on the legacy Ticketing implementation until its NavigatorReports wrapper is built.',
  },
  'submitters-locations': {
    icon: 'place',
    title: 'Submitters and Locations',
    description: 'Submitters and Locations is not migrated yet in the new NavigatorReports page.',
  },
  rca: {
    icon: 'hub',
    title: 'RCA',
    description: 'RCA will be migrated after the shared adapter and Overview path are proven stable.',
  },
};

const createTicketingAnalyticsPageStyles = (isDark: boolean, colors: any) =>
  StyleSheet.create({
    container: {
      backgroundColor: isDark && Platform.OS !== 'web' ? colors.background.page : '#F5F9FF',
      paddingTop: 24,
      paddingHorizontal: 25,
      paddingBottom: 24,
    },
    breadcrumbRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      marginBottom: 8,
    },
    breadcrumbLinkWrap: {
      paddingVertical: 2,
      paddingRight: 2,
    },
    breadcrumbLink: {
      fontSize: 13,
      fontFamily: FontFamily.medium,
      color: '#0B5FC6',
    },
    breadcrumbCurrent: {
      fontSize: 13,
      fontFamily: FontFamily.medium,
      color: isDark && Platform.OS !== 'web' ? colors.text.secondary : '#7A8088',
    },
    pageTitle: {
      fontSize: 32,
      lineHeight: 40,
      fontFamily: FontFamily.bold,
      color: isDark && Platform.OS !== 'web' ? colors.text.primary : '#2D2F33',
      marginBottom: 12,
    },
  });

const TicketingAnalyticsDashboard: React.FC<TicketingAnalyticsDashboardProps> = ({ onBack }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const pageStyles = createTicketingAnalyticsPageStyles(isDark, colors);
  const dispatch = useAppDispatch();
  const storedPayload = useSelector((state: RootState) => state.ticketingAnalyticsFilters.latestPayload);

  /**
   * Maps a raw API/network error to a user-friendly message.
   * Axios and fetch both surface low-level connectivity problems as "Network Error"
   * or "Failed to fetch" — neither is meaningful to the end user.
   * Declared early so it can be used by every component-level useAsyncSection call.
   */
  const toFriendlyError = (err: any, fallback: string): string => {
    const raw = (err?.message ?? '').toLowerCase().trim();
    if (
      !raw ||
      raw === 'network error' ||
      raw.includes('failed to fetch') ||
      raw.includes('networkerror') ||
      raw.includes('network request failed')
    ) {
      return 'Connection lost. Check your network and try again.';
    }
    return err?.message ?? fallback;
  };

  // Build category / sub-category ID → name lookup maps from the Redux form config.
  // The backend analytics endpoints return numeric categoryId / subCategoryId values.
  // These maps are passed to fetch functions so numeric IDs are resolved to display names.
  const submitTicketCategoryConfig = useSelector(
    (state: RootState) => state.ticketingPortal.submitTicketCategoryConfig,
  );

  // Fetch form config if not loaded for the current location/role, so we have categoryIdToName mappings
  const categoryBetaParams = useSelector(selectCategoryBetaParams);
  const categoryContextKey = useSelector(selectCategoryContextKey);
  const categoryStale = useSelector(selectIsCategoryConfigStale);
  useEffect(() => {
    let cancelled = false;
    if (categoryStale) {
      const apiClient = createApiClient();
      const betaParams = getCategoryBetaParams(store.getState() as RootState);
      apiClient
        .get<{ id: number; name: string; displayOrder: number; subCategories: { id: number; name: string; displayOrder: number }[] }[]>(
          '/ticketing/form-config/categories',
          { params: categoryBetaParams }
        )
        .then((response) => {
          if (cancelled) return;
          const config = response?.data ?? [];
          if (Array.isArray(config) && config.length > 0) {
            console.log('[TicketingAnalyticsDashboard] Fetched category config:', config.slice(0, 3)); // Log first 3
            // Dispatch to Redux to cache the config
            dispatch(
              setSubmitTicketFormConfigSuccess({
                categories: config.map((c) => c.name),
                subCategoryMap: Object.fromEntries(
                  config.map((c) => [
                    c.name,
                    c.subCategories.map((s) => s.name),
                  ])
                ),
                categoryConfig: config,
                contextKey: categoryContextKey,
              })
            );
          }
        })
        .catch((err) => {
          console.warn('[TicketingAnalyticsDashboard] Failed to fetch category config:', err);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [dispatch, categoryStale, categoryBetaParams, categoryContextKey]);

  const categoryLookup = useMemo<CategoryLookup>(() => {
    const categoryIdToName: Record<number, string> = {};
    const subCategoryIdToName: Record<number, string> = {};
    submitTicketCategoryConfig.forEach((cat) => {
      categoryIdToName[cat.id] = cat.name;
      cat.subCategories.forEach((sub) => {
        subCategoryIdToName[sub.id] = sub.name;
      });
    });
    if (Object.keys(categoryIdToName).length > 0) {
      console.log('[TicketingAnalyticsDashboard] Category lookup ready:', {
        categories: Object.keys(categoryIdToName).length,
        subCategories: Object.keys(subCategoryIdToName).length,
      });
    }
    return { categoryIdToName, subCategoryIdToName };
  }, [submitTicketCategoryConfig]);

  // ─── Read the header's selected location from Redux (set by DLOHeader) ──
  const headerSelectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
  const initialLocationFromHeader = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!headerSelectedLocation) return [];
    const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';
    if (!locId && !locName) return [];
    return [{ id: locId, displayText: locName }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount — don't react to later header changes
  const hasAutoPopulatedRef = useRef(initialLocationFromHeader.length > 0);

  const initialTimePeriod = storedPayload?.data?.timePeriod ?? 'week';
  const initialSelectedFilters = storedPayload?.data?.selectedFilters ?? createEmptySelection();
  const initialCustomDateRange = storedPayload?.data?.customDateRange ?? createEmptyCustomDateRange();

  const [activeTab, setActiveTab] = useState<TicketingAnalyticsTab>('overview');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(initialTimePeriod);
  const [appliedTimePeriod, setAppliedTimePeriod] = useState<TimePeriod>(initialTimePeriod);
  const [customDateRange, setCustomDateRange] = useState<TicketingAnalyticsCustomDateRange>(cloneCustomDateRange(initialCustomDateRange));
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState<TicketingAnalyticsCustomDateRange>(cloneCustomDateRange(initialCustomDateRange));
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<TicketingAnalyticsSelectedFilters>(cloneFilters(initialSelectedFilters));
  const [appliedFilters, setAppliedFilters] = useState<TicketingAnalyticsSelectedFilters>(cloneFilters(initialSelectedFilters));

  // ─── API-based region/market/location loading ──────────────────────────────
  const [regionOptions, setRegionOptions] = useState<KPIFilterDropdownItem[]>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<KPIFilterDropdownItem[]>([]);
  const [apiLocationOptions, setApiLocationOptions] = useState<KPIFilterDropdownItem[]>([]);

  // Normalize and derive selected region/market IDs for location filtering
  const normalizeFilterValue = (value: unknown): string => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const selectedRegionIds: string[] = useMemo(() => {
    if (selectedRegions.length === 0) return ['all'];
    return selectedRegions.map(r => normalizeFilterValue(r.id));
  }, [selectedRegions]);
  const selectedMarketIds: string[] = useMemo(() => {
    if (selectedMarkets.length === 0) return ['all'];
    return selectedMarkets.map(m => normalizeFilterValue(m.id));
  }, [selectedMarkets]);

  // Load admin locations and filter by selected region/market
  const { locations: adminLocations } = useAdminLocations();

  // filteredLocationOptions: locations scoped to the *applied* region + market filters.
  // Used for API calls — always reflects what was committed with "Apply".
  const filteredLocationOptions: KPIFilterDropdownItem[] = useMemo(
    () => adminLocations
      .filter(loc => {
        const matchesRegion =
          appliedFilters.regions.length === 0 ||
          appliedFilters.regions.some(r => normalizeFilterValue(r.id) === normalizeFilterValue(loc.region));
        const matchesMarket =
          appliedFilters.markets.length === 0 ||
          appliedFilters.markets.some(m => normalizeFilterValue(m.id) === normalizeFilterValue(loc.market));
        return matchesRegion && matchesMarket;
      })
      .map(loc => ({ id: loc.locName, displayText: loc.locName }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText)),
    [adminLocations, appliedFilters.regions, appliedFilters.markets],
  );

  // draftLocationOptions: locations scoped to the *draft* (selected but not yet applied)
  // region + market. Used only for the location dropdown in the filter panel UI so the
  // user sees the cascaded narrowing while making selections (before hitting Apply).
  const draftLocationOptions: KPIFilterDropdownItem[] = useMemo(
    () => adminLocations
      .filter(loc => {
        const matchesRegion =
          selectedRegionIds.includes('all') || selectedRegionIds.includes(normalizeFilterValue(loc.region));
        const matchesMarket =
          selectedMarketIds.includes('all') || selectedMarketIds.includes(normalizeFilterValue(loc.market));
        return matchesRegion && matchesMarket;
      })
      .map(loc => ({ id: loc.locName, displayText: loc.locName }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText)),
    [adminLocations, selectedRegionIds, selectedMarketIds],
  );

  // ─── Market options derived from adminLocations (filtered by selected regions)
  const marketOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations
      .filter((loc) =>
        selectedRegions.length === 0 ||
        selectedRegions.some(
          (r) => normalizeFilterValue(r.id) === normalizeFilterValue(loc.region),
        ),
      )
      .forEach((loc) => {
        const m = loc.market?.trim();
        if (m && !seen.has(m)) {
          seen.add(m);
          options.push({ id: m, displayText: m });
        }
      });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations, selectedRegions]);

  // ─── Applied region/market IDs (from committed appliedFilters, not draft) ──────
  // These drive the scoped fallback values passed to API calls.
  // The draft selectedRegions/selectedMarkets only control the filter panel UI
  // cascading (dropdown options narrowing). At API-call time we use the *applied* state.
  const appliedRegionIds: string[] = useMemo(
    () => appliedFilters.regions.map((r) => normalizeFilterValue(r.id)),
    [appliedFilters.regions],
  );

  // ─── allRegionOptions: all regions — used as the API fallback when no region is
  // selected. When regions ARE selected, buildSemanticFilterBody uses
  // appliedFilters.regions directly and ignores this fallback value.
  const allRegionOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations.forEach((loc) => {
      const r = loc.region?.trim();
      if (r && !seen.has(r)) {
        seen.add(r);
        options.push({ id: r, displayText: r });
      }
    });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations]);

  // ─── scopedMarketOptions: API fallback for markets, scoped to applied regions ──
  // Behaviour:
  //   • No region applied + no market applied  → ALL markets   (initial load)
  //   • Region(s) applied + no market applied  → Only markets within those regions
  //     (so the API receives "all markets under the selected region(s)")
  //   • Market(s) applied                      → appliedFilters.markets is non-empty;
  //     buildSemanticFilterBody uses that directly; this fallback is not reached.
  const scopedMarketOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations
      .filter((loc) =>
        appliedRegionIds.length === 0 ||
        appliedRegionIds.includes(normalizeFilterValue(loc.region)),
      )
      .forEach((loc) => {
        const m = loc.market?.trim();
        if (m && !seen.has(m)) {
          seen.add(m);
          options.push({ id: m, displayText: m });
        }
      });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations, appliedRegionIds]);

  // Pull region / market / location / category / sub-category values from Redux
  const reduxRegions = useSelector((state: RootState) => selectDistinctRegions(state));
  const reduxMarkets = useSelector((state: RootState) => selectDistinctMarkets(state));
  const reduxLocationNames = useSelector((state: RootState) => selectDistinctLocationNames(state));
  const reduxCategories = useSelector((state: RootState) => selectDistinctTicketingCategories(state));
  const reduxSubCategoriesWithCat = useSelector((state: RootState) => selectTicketingSubCategoriesWithCategory(state));
  const appPeriodWeekData = useSelector((state: RootState) => state.appData.periodWeek?.data);

  // Convert plain string arrays → KPIFilterDropdownItem[]
  // Use the value itself as the id so selections remain stable across re-renders.
  const reduxRegionItems = useMemo<KPIFilterDropdownItem[]>(
    () => reduxRegions.map((r) => ({ id: r, displayText: r })),
    [reduxRegions],
  );
  const reduxMarketItems = useMemo<KPIFilterDropdownItem[]>(
    () => reduxMarkets.map((m) => ({ id: m, displayText: m })),
    [reduxMarkets],
  );
  const reduxLocationItems = useMemo<KPIFilterDropdownItem[]>(
    () => reduxLocationNames.map((l) => ({ id: l, displayText: l })),
    [reduxLocationNames],
  );
  // For categories: map display names to numeric IDs from config
  const reduxCategoryItems = useMemo<KPIFilterDropdownItem[]>(
    () => reduxCategories.map((categoryName) => {
      const categoryConfig = submitTicketCategoryConfig.find(
        (c) => c.name === categoryName
      );
      return {
        id: categoryConfig ? String(categoryConfig.id) : categoryName,
        displayText: categoryName,
      };
    }),
    [reduxCategories, submitTicketCategoryConfig],
  );
  // Sub-category items already use numeric IDs from the config
  const reduxSubCategoryItems = useMemo<KPIFilterDropdownItem[]>(
    () => reduxSubCategoriesWithCat.map((s) => ({
      id: String(s.id),
      displayText: s.name,
      subLabel: s.categoryName,
    })),
    [reduxSubCategoriesWithCat],
  );
  /**
   * Category chip options for the Lifecycle "Resolution Time by Status" card.
   * Sourced from the same category list as the top CATEGORY filter dropdown,
   * with an "All" chip prepended.
   */
  const lifecycleCategoryOptions = useMemo<LifecycleStatusViewOption[]>(
    () => [
      LIFECYCLE_STATUS_VIEW_ALL_OPTION,
      ...reduxCategoryItems.map((item) => ({ key: String(item.id), label: item.displayText })),
    ],
    [reduxCategoryItems],
  );
  /** Assigned-member items fetched live from /api/ticketing/tickets */
  const [assignedMemberItems, setAssignedMemberItems] = useState<KPIFilterDropdownItem[]>([]);
  /** True once the assigned-members fetch has completed (success or error) */
  const [assignedMembersLoaded, setAssignedMembersLoaded] = useState(false);

  // ─── Global Team selector (separate from the filter bar) ──────────────────
  const { isSustainTeamMember } = useTicketingAccess();
  const userInfo = useSelector((state: RootState) => state.appData.userInfo);
  const sustainTeamMembers = useSelector((state: RootState) => state.ticketingPortal.sustainTeamMembers);
  const currentUserEmail = (userInfo?.email ?? '').toLowerCase();

  const [teamOptions, setTeamOptions] = useState<TicketingAnalyticsTeamOption[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamScope, setTeamScope] = useState<TicketingAnalyticsTeamScope | null>(null);

  useEffect(() => {
    let isMounted = true;
    setTeamsLoading(true);
    ticketingConfigApi
      .getTeams({ excludeWithoutOpenTickets: true })
      .then((teams) => {
        if (isMounted) {
          setTeamOptions(teams.map((t) => ({ id: String(t.id), name: t.name })));
        }
      })
      .catch(() => {
        if (isMounted) {
          setTeamOptions([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setTeamsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // The Sustain roster is needed to resolve "the team assigned to the current user"
  // (Sustain Team Management). Fetch it once if some other screen hasn't already.
  useEffect(() => {
    if (sustainTeamMembers.length === 0) {
      dispatch(fetchSustainTeamMembers() as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myAssignedTeamId = useMemo(() => {
    if (!isSustainTeamMember) return null;
    const me = sustainTeamMembers.find((m) => (m.userEmail ?? '').toLowerCase() === currentUserEmail);
    return me?.teamId != null ? String(me.teamId) : null;
  }, [isSustainTeamMember, sustainTeamMembers, currentUserEmail]);

  const isPqgcEBusiness = useMemo(() => /pqgc/i.test(userInfo?.roleName ?? ''), [userInfo?.roleName]);

  // Resolve the default Team scope once teams + role context are known.
  // Never defaults to the first dropdown entry — follows the Navigator role /
  // Sustain Team Management assignment rules from the Team selector spec.
  useEffect(() => {
    if (teamScope !== null || teamOptions.length === 0) return;

    if (isPqgcEBusiness) {
      const pqgcTeam = teamOptions.find((t) => /pqgc/i.test(t.name));
      if (pqgcTeam) {
        setTeamScope({ id: pqgcTeam.id, name: pqgcTeam.name });
        return;
      }
    }

    if (myAssignedTeamId != null) {
      const myTeam = teamOptions.find((t) => t.id === myAssignedTeamId);
      if (myTeam) {
        setTeamScope({ id: myTeam.id, name: myTeam.name });
        return;
      }
    }

    const defaultTeam =
      teamOptions.find((t) => /pbna\s*business\s*sustain/i.test(t.name)) ??
      teamOptions.find((t) => /pbna\s*business/i.test(t.name));
    setTeamScope(
      defaultTeam ? { id: defaultTeam.id, name: defaultTeam.name } : { id: null, name: 'All' },
    );
  }, [teamOptions, myAssignedTeamId, isPqgcEBusiness, teamScope]);

  /** `teamIds` request param derived from the selected scope — undefined (omitted) means "All". */
  const teamIdsParam = useMemo(
    () => (teamScope?.id ? [teamScope.id] : undefined),
    [teamScope?.id],
  );

  // ─── Tertiary filter catalog — mirrors the Category/Sub-Category ID↔name pattern ──
  const [tertiaryOptions, setTertiaryOptions] = useState<KPIFilterDropdownItem[]>([]);

  useEffect(() => {
    if (!submitTicketCategoryConfig || submitTicketCategoryConfig.length === 0) {
      return;
    }
    let isMounted = true;
    const subCategoryIds = submitTicketCategoryConfig.flatMap((cat) => cat.subCategories.map((sub) => sub.id));

    (async () => {
      try {
        const tertiaryLists = await Promise.all(
          subCategoryIds.map((id) => ticketingConfigApi.getTertiaryCategories(id).catch(() => [])),
        );
        if (!isMounted) return;
        const seen = new Map<string, string>();
        tertiaryLists.flat().forEach((t) => {
          if (!seen.has(String(t.id))) {
            seen.set(String(t.id), t.name);
          }
        });
        const options = Array.from(seen.entries())
          .map(([id, name]) => ({ id, displayText: name }))
          .sort((a, b) => a.displayText.localeCompare(b.displayText));
        setTertiaryOptions(options);
      } catch {
        if (isMounted) {
          setTertiaryOptions([]);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [submitTicketCategoryConfig]);

  // ─── Overview tab: one independent { data, loading, error, retry } lifecycle
  // per card, via useAsyncSection. A failure in one section (e.g. Aging
  // Tickets — the intentionally-failing SEMANTIC_AGING_ENDPOINT test case)
  // never affects the others; each section's Retry only re-calls its own API.
  const overviewEnabled = activeTab === 'overview';
  const overviewCoreMetrics = useAsyncSection(
    () => fetchTicketingOverviewCoreMetrics({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: overviewEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load overview metrics.') },
  );
  const overviewStatusBreakdown = useAsyncSection(
    () => fetchTicketingOverviewStatusBreakdown({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: overviewEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load status breakdown.') },
  );
  const overviewAgingTickets = useAsyncSection(
    () => fetchTicketingOverviewAgingTickets({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: overviewEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load aging tickets.') },
  );
  const overviewCategoryBreakdown = useAsyncSection(
    () => fetchTicketingOverviewCategoryBreakdown({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.categoryIdToName, teamScope?.id],
    { enabled: overviewEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load tickets by category.') },
  );
  const overviewDuplicateActivity = useAsyncSection(
    () => fetchTicketingOverviewDuplicateActivity({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: overviewEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load duplicate activity.') },
  );

  const [priorityView, setPriorityView] = useState<PriorityView>('total');
  const [lifecycleSelectedCategories, setLifecycleSelectedCategories] = useState<string[]>(['all']);
  /** Independent loading/data state for the category-scoped Resolution Time by Status card. */
  const [statusByCategoryLoading, setStatusByCategoryLoading] = useState(false);
  const [statusByCategoryError, setStatusByCategoryError] = useState<string | null>(null);
  const [statusByCategoryRows, setStatusByCategoryRows] = useState<LifecycleStatusData[]>([]);
  const [teamWorkloadLoading, setTeamWorkloadLoading] = useState(false);
  const [teamWorkloadError, setTeamWorkloadError] = useState<string | null>(null);
  const [teamWorkloadData, setTeamWorkloadData] = useState<Awaited<ReturnType<typeof fetchNavigatorTicketingTeamWorkload>> | null>(null);
  const [categoriesBreakdownView, setCategoriesBreakdownView] = useState<'total' | 'open'>('total');
  const [categoriesTopSubView, setCategoriesTopSubView] = useState<'total' | 'open'>('total');
  const [categoriesSelectedCategory, setCategoriesSelectedCategory] = useState<number | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [categoriesData, setCategoriesData] = useState<Awaited<ReturnType<typeof fetchNavigatorTicketingCategories>> | null>(null);
  const [submittersLocationsLoading, setSubmittersLocationsLoading] = useState(false);
  const [submittersLocationsError, setSubmittersLocationsError] = useState<string | null>(null);
  const [submittersLocationsData, setSubmittersLocationsData] = useState<Awaited<ReturnType<typeof fetchNavigatorTicketingSubmittersLocations>> | null>(null);
  const [rcaLoading, setRcaLoading] = useState(false);
  const [rcaError, setRcaError] = useState<string | null>(null);
  const [rcaData, setRcaData] = useState<Awaited<ReturnType<typeof fetchNavigatorTicketingRca>> | null>(null);

  // ─── Volume & Trends tab: single shared endpoint, sliced into 5 per-card
  // sections. All 5 cards genuinely come from one semantic call, so they share
  // fate (fail/retry together) — but each still gets its own SectionRetry UI,
  // consistent with every other tab, instead of a tab-wide error blanking.
  const volumeEnabled = activeTab === 'volume-trends';
  const volumeSection = useAsyncSection(
    () => fetchNavigatorTicketingVolumeTrends({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: volumeEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load ticketing volume and trends.') },
  );
  const weeklySection = useMemo(() => ({
    data: volumeSection.data?.weeklyTicketVolume ?? null,
    loading: volumeSection.loading,
    error: volumeSection.error,
    retry: volumeSection.retry,
  }), [volumeSection.data, volumeSection.loading, volumeSection.error, volumeSection.retry]);
  const ticketsByPrioritySection = useMemo(() => ({
    data: volumeSection.data?.ticketsByPriority ?? null,
    loading: volumeSection.loading,
    error: volumeSection.error,
    retry: volumeSection.retry,
  }), [volumeSection.data, volumeSection.loading, volumeSection.error, volumeSection.retry]);
  const resolutionByPrioritySection = useMemo(() => ({
    data: volumeSection.data?.resolutionTimeByPriority ?? null,
    loading: volumeSection.loading,
    error: volumeSection.error,
    retry: volumeSection.retry,
  }), [volumeSection.data, volumeSection.loading, volumeSection.error, volumeSection.retry]);
  const submissionHeatMapSection = useMemo(() => ({
    data: volumeSection.data?.submissionHeatMap ?? null,
    loading: volumeSection.loading,
    error: volumeSection.error,
    retry: volumeSection.retry,
  }), [volumeSection.data, volumeSection.loading, volumeSection.error, volumeSection.retry]);
  const resolutionHeatMapSection = useMemo(() => ({
    data: volumeSection.data?.resolutionHeatMap ?? null,
    loading: volumeSection.loading,
    error: volumeSection.error,
    retry: volumeSection.retry,
  }), [volumeSection.data, volumeSection.loading, volumeSection.error, volumeSection.retry]);

  // ─── Lifecycle tab: one independent { data, loading, error, retry }
  // lifecycle per card. Stale Tickets (its own endpoint) failing no longer
  // blanks Overview/Resolving Party/Resolution Time per Category.
  const lifecycleEnabled = activeTab === 'lifecycle';
  const lifecycleOverviewSection = useAsyncSection(
    () => fetchTicketingLifecycleOverview({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: lifecycleEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load lifecycle overview.') },
  );
  const lifecycleResolvingPartySection = useAsyncSection(
    () => fetchTicketingLifecycleResolvingParty({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id],
    { enabled: lifecycleEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load resolving party.') },
  );
  const lifecycleStaleTicketsSection = useAsyncSection(
    () => fetchTicketingLifecycleStaleTickets({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.subCategoryIdToName, teamScope?.id],
    { enabled: lifecycleEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load stale tickets.') },
  );
  const lifecycleResolutionTimePerCategorySection = useAsyncSection(
    () => fetchTicketingLifecycleResolutionTimePerCategory({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup),
    [appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.categoryIdToName, categoryLookup?.subCategoryIdToName, teamScope?.id],
    { enabled: lifecycleEnabled, toFriendlyError: (err) => toFriendlyError(err, 'Failed to load resolution time per category.') },
  );

  // Each tab owns its own request, so the applied-filters summary is gated by the
  // active tab's state: it appears once that tab's data is ready and hides on error.
  // Overview's error is intentionally always null here: each of its cards now
  // owns its own independent error/retry state (see the useAsyncSection calls
  // above), so a single card failing must never trigger a tab-wide error banner.
  const tabRequestState = useMemo<Record<TicketingAnalyticsTab, TicketingTabRequestState>>(() => ({
    'overview': {
      loading: overviewCoreMetrics.loading || overviewStatusBreakdown.loading || overviewAgingTickets.loading || overviewCategoryBreakdown.loading || overviewDuplicateActivity.loading,
      error: null,
    },
    'volume-trends': { loading: volumeSection.loading, error: null },
    'lifecycle': {
      loading: lifecycleOverviewSection.loading || lifecycleResolvingPartySection.loading || lifecycleStaleTicketsSection.loading || lifecycleResolutionTimePerCategorySection.loading || statusByCategoryLoading,
      error: null,
    },
    'team-workload': { loading: teamWorkloadLoading, error: teamWorkloadError },
    'categories': { loading: categoriesLoading, error: categoriesError },
    'submitters-locations': { loading: submittersLocationsLoading, error: submittersLocationsError },
    'rca': { loading: rcaLoading, error: rcaError },
  }), [
    overviewCoreMetrics.loading, overviewStatusBreakdown.loading, overviewAgingTickets.loading, overviewCategoryBreakdown.loading, overviewDuplicateActivity.loading,
    volumeSection.loading,
    lifecycleOverviewSection.loading, lifecycleResolvingPartySection.loading, lifecycleStaleTicketsSection.loading, lifecycleResolutionTimePerCategorySection.loading, statusByCategoryLoading,
    teamWorkloadLoading, teamWorkloadError,
    categoriesLoading, categoriesError,
    submittersLocationsLoading, submittersLocationsError,
    rcaLoading, rcaError,
  ]);

  const activeTabRequestState = tabRequestState[activeTab];

  // Derived from *applied* state so the summary always describes the fetched data,
  // never an in-progress draft selection.
  const summary = useMemo(() => getTicketingSummary({
    appliedFilters,
    appliedTimePeriod,
    appliedCustomDateRange,
    periodWeekContext: appPeriodWeekData,
  }), [appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData]);

  const showSummary = !activeTabRequestState?.loading && !activeTabRequestState?.error;

  // ─── Export tickets state ─────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false);

  const hasChanges = useMemo(() => {
    return timePeriod !== appliedTimePeriod
      || !customDateRangesEqual(customDateRange, appliedCustomDateRange)
      || !selectionsEqual(selectedFilters.regions, appliedFilters.regions)
      || !selectionsEqual(selectedFilters.markets, appliedFilters.markets)
      || !selectionsEqual(selectedFilters.locations, appliedFilters.locations)
      || !selectionsEqual(selectedFilters.categories, appliedFilters.categories)
      || !selectionsEqual(selectedFilters.subCategories, appliedFilters.subCategories)
      || !selectionsEqual(selectedFilters.tertiaries, appliedFilters.tertiaries)
      || !selectionsEqual(selectedFilters.statuses, appliedFilters.statuses)
      || !selectionsEqual(selectedFilters.resolutionPaths, appliedFilters.resolutionPaths)
      || !selectionsEqual(selectedFilters.urgencies, appliedFilters.urgencies)
      || !selectionsEqual(selectedFilters.assignedTo, appliedFilters.assignedTo);
  }, [timePeriod, appliedTimePeriod, customDateRange, appliedCustomDateRange, selectedFilters, appliedFilters]);

  // Check if custom range is selected but dates are missing
  const isCustomRangeValid = useMemo(() => {
    if (timePeriod !== 'custom') {
      return true; // Not using custom range, so it's valid
    }
    // Custom range is selected, both dates must be provided
    return customDateRange.startDate !== null && customDateRange.endDate !== null;
  }, [timePeriod, customDateRange]);

  // Apply button should be enabled only if there are changes AND (not custom range OR custom range is valid)
  const canApplyFilters = hasChanges && isCustomRangeValid;

  // True when the user has applied any narrowing filter. Used by visualization
  // components to decide between the "no underlying data" and "zero results
  // from filters" empty-state messages.
  const hasActiveFilters = useMemo(() => {
    return (
      appliedFilters.regions.length > 0 ||
      appliedFilters.markets.length > 0 ||
      appliedFilters.locations.length > 0 ||
      appliedFilters.categories.length > 0 ||
      appliedFilters.subCategories.length > 0 ||
      appliedFilters.tertiaries.length > 0 ||
      appliedFilters.statuses.length > 0 ||
      appliedFilters.resolutionPaths.length > 0 ||
      appliedFilters.urgencies.length > 0 ||
      appliedFilters.assignedTo.length > 0
    );
  }, [appliedFilters]);

  // ─── Fetch regions on mount ──────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const loadRegions = async () => {
      setIsLoadingRegions(true);
      try {
        const regions = await locationApi.getRegions();
        if (!isMounted) {
          return;
        }

        const options = regions
          .filter(regionName => regionName?.trim().toLowerCase() !== 'all')
          .map(regionName => ({ id: regionName, displayText: regionName }));
        setRegionOptions(options);
      } catch {
        if (!isMounted) {
          return;
        }
        setRegionOptions([]);
      } finally {
        if (isMounted) {
          setIsLoadingRegions(false);
        }
      }
    };

    void loadRegions();

    return () => {
      isMounted = false;
    };
  }, []);

  

  // ─── Auto-populate location from header on initial page load ──────────────
  useEffect(() => {
    // Only auto-populate header location (if available)
    // Keep regions/markets empty - API will use availableRegions/Markets fallback
    // This keeps UI showing "All" while API gets all data via fallback logic
    if (hasAutoPopulatedRef.current) return;
    if (!headerSelectedLocation) return;
    if (filteredLocationOptions.length === 0) return;

    const locId = String(
      headerSelectedLocation.id || headerSelectedLocation.locationId || ''
    );
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';

    // Find matching location in the available dropdown options
    const matchingOption = filteredLocationOptions.find(
      opt => opt.displayText === locName || opt.id === locName
    );

    if (matchingOption) {
      // Only set the header location, leave regions/markets empty so API uses fallback
      setSelectedFilters(prev => ({
        ...prev,
        locations: [matchingOption]
      }));
      setAppliedFilters(prev => ({
        ...prev,
        locations: [matchingOption]
      }));
      hasAutoPopulatedRef.current = true;
    }
  }, [headerSelectedLocation]);

  // Fetch distinct assigned members from GET /ticketing/tickets on mount.
  // These populate the "Assigned To" filter dropdown with real user names.
  // assignedMembersLoaded is set true when the fetch completes so we never
  // fall back to mock data after the real response arrives.
  useEffect(() => {
    let isMounted = true;

    const loadAssignedMembers = async () => {
      const members = await fetchTicketingAssignedMembers();
      if (isMounted) {
        setAssignedMemberItems(members);
        setAssignedMembersLoaded(true);
      }
    };

    void loadAssignedMembers();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'rca') return;
    let isMounted = true;

    const loadRca = async () => {
      setRcaLoading(true);
      setRcaError(null);
      setRcaData(null);
      try {
        const data = await fetchNavigatorTicketingRca({
          timePeriod: appliedTimePeriod,
          customDateRange: appliedCustomDateRange,
          timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
          selectedFilters: appliedFilters,
          availableLocations: filteredLocationOptions,
          availableRegions: allRegionOptions,
          availableMarkets: scopedMarketOptions,
          teamIds: teamIdsParam,
        }, categoryLookup);
        if (isMounted) {
          setRcaData(data);
        }
      } catch (error: any) {
        if (isMounted) {
          setRcaError(toFriendlyError(error, 'Failed to load ticketing RCA.'));
        }
      } finally {
        if (isMounted) {
          setRcaLoading(false);
        }
      }
    };

    void loadRca();

    return () => {
      isMounted = false;
    };
  }, [activeTab, appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.categoryIdToName, teamScope?.id]);

  useEffect(() => {
    if (activeTab !== 'submitters-locations') return;
    let isMounted = true;

    const loadSubmittersLocations = async () => {
      setSubmittersLocationsLoading(true);
      setSubmittersLocationsError(null);
      setSubmittersLocationsData(null);
      try {
        const data = await fetchNavigatorTicketingSubmittersLocations({
          timePeriod: appliedTimePeriod,
          customDateRange: appliedCustomDateRange,
          timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
          selectedFilters: appliedFilters,
          availableLocations: filteredLocationOptions,
          availableRegions: allRegionOptions,
          availableMarkets: scopedMarketOptions,
          teamIds: teamIdsParam,
        }, categoryLookup);
        if (isMounted) {
          setSubmittersLocationsData(data);
        }
      } catch (error: any) {
        if (isMounted) {
          setSubmittersLocationsError(toFriendlyError(error, 'Failed to load ticketing submitters and locations.'));
        }
      } finally {
        if (isMounted) {
          setSubmittersLocationsLoading(false);
        }
      }
    };

    void loadSubmittersLocations();

    return () => {
      isMounted = false;
    };
  }, [activeTab, appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.categoryIdToName, teamScope?.id]);

  useEffect(() => {
    if (activeTab !== 'categories') return;
    let isMounted = true;

    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);
      setCategoriesData(null);
      try {
        const data = await fetchNavigatorTicketingCategories({
          timePeriod: appliedTimePeriod,
          customDateRange: appliedCustomDateRange,
          timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
          selectedFilters: appliedFilters,
          availableLocations: filteredLocationOptions,
          availableRegions: allRegionOptions,
          availableMarkets: scopedMarketOptions,
          teamIds: teamIdsParam,
        }, categoryLookup);
        if (isMounted) {
          setCategoriesData(data);
        }
      } catch (error: any) {
        if (isMounted) {
          setCategoriesError(toFriendlyError(error, 'Failed to load ticketing categories.'));
        }
      } finally {
        if (isMounted) {
          setCategoriesLoading(false);
        }
      }
    };

    void loadCategories();

    return () => {
      isMounted = false;
    };
  }, [activeTab, appliedFilters, appliedTimePeriod, appliedCustomDateRange, categoryLookup?.categoryIdToName, teamScope?.id]);

  useEffect(() => {
    if (activeTab !== 'team-workload') return;
    let isMounted = true;

    const loadTeamWorkload = async () => {
      setTeamWorkloadLoading(true);
      setTeamWorkloadError(null);
      setTeamWorkloadData(null);
      try {
        const data = await fetchNavigatorTicketingTeamWorkload({
          timePeriod: appliedTimePeriod,
          customDateRange: appliedCustomDateRange,
          timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
          selectedFilters: appliedFilters,
          availableLocations: filteredLocationOptions,
          availableRegions: allRegionOptions,
          availableMarkets: scopedMarketOptions,
          teamIds: teamIdsParam,
        });
        if (isMounted) {
          setTeamWorkloadData(data);
        }
      } catch (error: any) {
        if (isMounted) {
          setTeamWorkloadError(toFriendlyError(error, 'Failed to load ticketing team workload.'));
        }
      } finally {
        if (isMounted) {
          setTeamWorkloadLoading(false);
        }
      }
    };

    void loadTeamWorkload();

    return () => {
      isMounted = false;
    };
  }, [activeTab, appliedFilters, appliedTimePeriod, appliedCustomDateRange, teamScope?.id]);

  // Independent fetch for the "Resolution Time by Status" card: refetches
  // only this card (with its own skeleton loading) whenever the user changes
  // the card's category chip, or when the page-level filters/time period change.
  // Gated on activeTab === 'lifecycle' so it does not fire on other tabs.
  useEffect(() => {
    if (activeTab !== 'lifecycle') return;
    let isMounted = true;

    const loadStatusByCategory = async () => {
      setStatusByCategoryLoading(true);
      setStatusByCategoryError(null);
      try {
        const rows = await fetchTicketingStatusAvgTimeByCategory(
          {
            timePeriod: appliedTimePeriod,
            customDateRange: appliedCustomDateRange,
            timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
            selectedFilters: appliedFilters,
            availableLocations: filteredLocationOptions,
            availableRegions: allRegionOptions,
            availableMarkets: scopedMarketOptions,
            teamIds: teamIdsParam,
          },
          lifecycleSelectedCategories,
        );
        if (isMounted) {
          setStatusByCategoryRows(rows);
        }
      } catch (error: any) {
        if (isMounted) {
          setStatusByCategoryError(toFriendlyError(error, 'Failed to load resolution time by status.'));
        }
      } finally {
        if (isMounted) {
          setStatusByCategoryLoading(false);
        }
      }
    };

    void loadStatusByCategory();

    return () => {
      isMounted = false;
    };
  }, [activeTab, appliedFilters, appliedTimePeriod, appliedCustomDateRange, lifecycleSelectedCategories, teamScope?.id]);

  useEffect(() => {
    const selected = ticketsByPrioritySection.data?.selectedView;
    if (selected === 'open' || selected === 'total') {
      setPriorityView(selected);
    }
  }, [ticketsByPrioritySection.data?.selectedView]);

  useEffect(() => {
    const selectedView = categoriesData?.categoryBreakdown?.selectedView;
    if (selectedView === 'total' || selectedView === 'open') {
      setCategoriesBreakdownView(selectedView);
    }

    const selectedTopSub = categoriesData?.topSubCategories?.selectedView;
    if (selectedTopSub === 'total' || selectedTopSub === 'open') {
      setCategoriesTopSubView(selectedTopSub);
    }

    const selectedCategory = categoriesData?.categoryBreakdown?.selectedCategory;
    if (typeof selectedCategory === 'number' || selectedCategory === null) {
      setCategoriesSelectedCategory(selectedCategory);
    }
  }, [
    categoriesData?.categoryBreakdown?.selectedCategory,
    categoriesData?.categoryBreakdown?.selectedView,
    categoriesData?.topSubCategories?.selectedView,
  ]);

  // Status and resolution path items are static constants — compute once outside render
  const statusItems = getTicketingStatusItems();
  const resolutionPathItems = getTicketingResolutionPathItems();

  // ─── Per-tab retry callbacks ──────────────────────────────────────────────
  // Each callback re-runs only its own tab's API using the current applied
  // filter state. The `loading` guard prevents duplicate in-flight requests if

  // the user taps Retry multiple times quickly.
  // Note: Overview's cards each retry independently via their own
  // useAsyncSection `retry()` (see overviewCoreMetrics.retry, etc. above) —
  // no page-level retryOverview callback is needed anymore.

  const retryStatusByCategory = useCallback(() => {
    if (statusByCategoryLoading) return;
    setStatusByCategoryLoading(true);
    setStatusByCategoryError(null);
    fetchTicketingStatusAvgTimeByCategory({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      teamIds: teamIdsParam,
    }, lifecycleSelectedCategories)
      .then((rows) => setStatusByCategoryRows(rows))
      .catch((err: any) => setStatusByCategoryError(toFriendlyError(err, 'Failed to load resolution time by status.')))
      .finally(() => setStatusByCategoryLoading(false));
  }, [statusByCategoryLoading, appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData, lifecycleSelectedCategories, teamIdsParam]);

  const retryTeamWorkload = useCallback(() => {
    if (teamWorkloadLoading) return;
    setTeamWorkloadLoading(true);
    setTeamWorkloadError(null);
    setTeamWorkloadData(null);
    fetchNavigatorTicketingTeamWorkload({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    })
      .then((d) => setTeamWorkloadData(d))
      .catch((err: any) => setTeamWorkloadError(toFriendlyError(err, 'Failed to load ticketing team workload.')))
      .finally(() => setTeamWorkloadLoading(false));
  }, [teamWorkloadLoading, appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData, teamIdsParam]);

  const retryCategories = useCallback(() => {
    if (categoriesLoading) return;
    setCategoriesLoading(true);
    setCategoriesError(null);
    setCategoriesData(null);
    fetchNavigatorTicketingCategories({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup)
      .then((d) => setCategoriesData(d))
      .catch((err: any) => setCategoriesError(toFriendlyError(err, 'Failed to load ticketing categories.')))
      .finally(() => setCategoriesLoading(false));
  }, [categoriesLoading, appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData, categoryLookup, teamIdsParam]);

  const retrySubmittersLocations = useCallback(() => {
    if (submittersLocationsLoading) return;
    setSubmittersLocationsLoading(true);
    setSubmittersLocationsError(null);
    setSubmittersLocationsData(null);
    fetchNavigatorTicketingSubmittersLocations({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup)
      .then((d) => setSubmittersLocationsData(d))
      .catch((err: any) => setSubmittersLocationsError(toFriendlyError(err, 'Failed to load ticketing submitters and locations.')))
      .finally(() => setSubmittersLocationsLoading(false));
  }, [submittersLocationsLoading, appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData, categoryLookup, teamIdsParam]);

  const retryRca = useCallback(() => {
    if (rcaLoading) return;
    setRcaLoading(true);
    setRcaError(null);
    setRcaData(null);
    fetchNavigatorTicketingRca({
      timePeriod: appliedTimePeriod,
      customDateRange: appliedCustomDateRange,
      timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
      selectedFilters: appliedFilters,
      availableLocations: filteredLocationOptions,
      availableRegions: allRegionOptions,
      availableMarkets: scopedMarketOptions,
      teamIds: teamIdsParam,
    }, categoryLookup)
      .then((d) => setRcaData(d))
      .catch((err: any) => setRcaError(toFriendlyError(err, 'Failed to load ticketing RCA.')))
      .finally(() => setRcaLoading(false));
  }, [rcaLoading, appliedFilters, appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData, categoryLookup, teamIdsParam]);

  // ─── End retry callbacks ──────────────────────────────────────────────────

  const filterOptionsWithAll = useMemo(
    () => ({
      // Use API-loaded region/market/location options with cascading behavior
      regions: withAllOption(regionOptions),
      markets: withAllOption(marketOptions),
      // Use draftLocationOptions for the dropdown UI so the user sees cascaded
      // narrowing in real-time while selecting regions/markets (before Apply).
      locations: withAllOption(draftLocationOptions),
      categories: withAllOption(reduxCategoryItems),
      subCategories: withAllOption(reduxSubCategoryItems),
      tertiaries: withAllOption(tertiaryOptions),
      // Statuses sourced from TICKET_STATUS_OPTIONS — matches Ticket Detail status dropdown
      statuses: withAllOption(statusItems),
      // Resolution paths sourced from RESOLUTION_PATH_OPTIONS — matches Ticket Detail triage panel
      resolutionPaths: withAllOption(resolutionPathItems),
      urgencies: withAllOption(URGENCY_FILTER_ITEMS),
      // Assigned-to options:
      //   - Before the fetch completes: show only "All"
      //   - After the fetch completes: show live members (or only "All" if none)
      assignedTo: withAllOption(
        assignedMembersLoaded ? assignedMemberItems : [],
      ),
    }),
    [regionOptions, marketOptions, draftLocationOptions, reduxCategoryItems, reduxSubCategoryItems, tertiaryOptions, assignedMemberItems, assignedMembersLoaded, statusItems, resolutionPathItems],
  );

  const handleSetSelectedFilter = (key: TicketingAnalyticsFilterKey, value: KPIFilterDropdownItem[]) => {
    // Update the main selectedFilters state
    setSelectedFilters((current) => {
      const next = { ...current, [key]: value };
      dispatch(
        setTicketingAnalyticsFiltersPayload(
          buildTicketingAnalyticsFiltersPayload(timePeriod, cloneFilters(next), customDateRange, appPeriodWeekData),
        ),
      );
      return next;
    });

    // Also update the cascading region/market state for location filtering
    if (key === 'regions') {
      setSelectedRegions(value);
      // Reset market and location when region changes
      setSelectedMarkets([]);
      setSelectedFilters((current) => ({
        ...current,
        markets: [],
        locations: [],
      }));
    } else if (key === 'markets') {
      setSelectedMarkets(value);
    }
  };

  const handleTimePeriodChange = (period: TimePeriod) => {
    setTimePeriod(period);

    const nextCustomDateRange = period === 'custom' ? customDateRange : createEmptyCustomDateRange();
    if (period !== 'custom') {
      setCustomDateRange(nextCustomDateRange);
    }

    dispatch(
      setTicketingAnalyticsFiltersPayload(
        buildTicketingAnalyticsFiltersPayload(period, cloneFilters(selectedFilters), nextCustomDateRange, appPeriodWeekData),
      ),
    );
  };

  const handleCustomDateRangeChange = (range: TicketingAnalyticsCustomDateRange) => {
    setCustomDateRange(range);
    dispatch(
      setTicketingAnalyticsFiltersPayload(
        buildTicketingAnalyticsFiltersPayload(timePeriod, cloneFilters(selectedFilters), range, appPeriodWeekData),
      ),
    );
  };

  const handleReset = () => {
    // Reset draft UI state
    setTimePeriod(initialTimePeriod);
    setCustomDateRange(createEmptyCustomDateRange());
    setSelectedFilters(createEmptySelection());
    setSelectedRegions([]);
    setSelectedMarkets([]);
    // Clear all error states when filters are reset
    setTeamWorkloadError(null);
    setCategoriesError(null);
    setSubmittersLocationsError(null);
    setRcaError(null);
    // Reset the persisted/applied payload in Redux
    dispatch(resetTicketingAnalyticsFiltersPayload());

    // If the currently-applied filters are already the defaults, avoid re-triggering
    // the tab API to prevent unnecessary duplicate calls. Otherwise update the
    // applied state so the active tab's effect re-runs with default params.
    const defaults = createEmptySelection();
    const appliedIsDefault = (
      appliedTimePeriod === initialTimePeriod &&
      customDateRangesEqual(appliedCustomDateRange, createEmptyCustomDateRange()) &&
      selectionsEqual(appliedFilters.regions, defaults.regions) &&
      selectionsEqual(appliedFilters.markets, defaults.markets) &&
      selectionsEqual(appliedFilters.locations, defaults.locations) &&
      selectionsEqual(appliedFilters.categories, defaults.categories) &&
      selectionsEqual(appliedFilters.subCategories, defaults.subCategories) &&
      selectionsEqual(appliedFilters.tertiaries, defaults.tertiaries) &&
      selectionsEqual(appliedFilters.statuses, defaults.statuses) &&
      selectionsEqual(appliedFilters.resolutionPaths, defaults.resolutionPaths) &&
      selectionsEqual(appliedFilters.urgencies, defaults.urgencies) &&
      selectionsEqual(appliedFilters.assignedTo, defaults.assignedTo)
    );

    if (!appliedIsDefault) {
      setAppliedFilters(cloneFilters(createEmptySelection()));
      setAppliedTimePeriod(initialTimePeriod);
      setAppliedCustomDateRange(cloneCustomDateRange(createEmptyCustomDateRange()));
    }

    setOpenFilterKey(null);
  };

  const handleTabChange = (tab: TicketingAnalyticsTab) => {
    // Clear all error states when switching tabs
    setTeamWorkloadError(null);
    setCategoriesError(null);
    setSubmittersLocationsError(null);
    setRcaError(null);
    setActiveTab(tab);
  };

  // ─── Export handler ────────────────────────────────────────────────────────
  const handleExportData = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportTicketsData({
        timePeriod: appliedTimePeriod,
        customDateRange: appliedCustomDateRange,
        timePeriodFilter: buildTicketingTimePeriodFilter(appliedTimePeriod, appliedCustomDateRange, appPeriodWeekData),
        selectedFilters: appliedFilters,
        teamIds: teamIdsParam,
      });
    } catch (error: any) {
      Alert.alert(
        'Export Failed',
        error?.message ?? 'Unable to export data. Please try again.',
        [{ text: 'OK' }],
      );
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, appliedTimePeriod, appliedCustomDateRange, appliedFilters, appPeriodWeekData, teamIdsParam]);

  const handleApply = () => {
    // Validate custom date range
    if (timePeriod === 'custom') {
      if (!customDateRange.startDate || !customDateRange.endDate) {
        Alert.alert(
          'Invalid Date Range',
          'Please select both start and end dates for the custom range.',
          [{ text: 'OK' }],
        );
        return;
      }
    }

    const nextAppliedFilters = cloneFilters(selectedFilters);
    
    // NOTE: Do NOT auto-populate locations here for appliedFilters (used in UI summary).
    // Only user-explicitly-selected locations should appear in the summary.
    // Auto-population will happen in buildSemanticFilterBody when building the API payload.

    // Ensure categories use their numeric IDs from the config
    nextAppliedFilters.categories = nextAppliedFilters.categories.map((cat) => {
      const categoryConfig = submitTicketCategoryConfig.find(
        (c) => c.name === cat.displayText
      );
      return {
        ...cat,
        id: categoryConfig ? String(categoryConfig.id) : cat.id,
      };
    });

    // Subcategories already have numeric IDs, but ensure they're strings
    nextAppliedFilters.subCategories = nextAppliedFilters.subCategories.map((subCat) => ({
      ...subCat,
      id: String(subCat.id),
    }));
    
    // Clear all error states when filters are applied
    setTeamWorkloadError(null);
    setCategoriesError(null);
    setSubmittersLocationsError(null);
    setRcaError(null);
    
    setAppliedFilters(nextAppliedFilters);
    setAppliedTimePeriod(timePeriod);
    setAppliedCustomDateRange(cloneCustomDateRange(customDateRange));
    dispatch(
      setTicketingAnalyticsFiltersPayload(
        buildTicketingAnalyticsFiltersPayload(timePeriod, nextAppliedFilters, customDateRange, appPeriodWeekData),
      ),
    );
    setOpenFilterKey(null);
  };

  return (
    <View style={pageStyles.container}>
      <View style={pageStyles.breadcrumbRow}>
        <TouchableOpacity
          onPress={onBack}
          style={pageStyles.breadcrumbLinkWrap}
          accessibilityRole="button"
          accessibilityLabel="Back to reports"
        >
          <Text style={pageStyles.breadcrumbLink}>Ticketing Portal</Text>
        </TouchableOpacity>
        <MaterialIcons name="chevron-right" size={16} color="#8A8E94" />
        <Text style={pageStyles.breadcrumbCurrent}>Reporting and Analytics</Text>
      </View>

      <View style={styles.pageTitleRow}>
        <View style={styles.pageTitleLeftSection}>
          <MaterialIcons name="trending-up" size={40} color={isDark ? colors.brand.primary : '#005CBC'} style={styles.pageIcon} />
          <Text style={[pageStyles.pageTitle, styles.pageTitleFlex]}>Reporting and Analytics</Text>
        </View>
        <View style={styles.pageTitleRightSection}>
          <TeamSelectorDropdown
            teams={teamOptions}
            value={teamScope}
            onChange={setTeamScope}
            loading={teamsLoading || teamScope === null}
          />
          <TouchableOpacity
            style={[
              styles.exportButton,
              isDark && { backgroundColor: colors.background.surface, borderColor: colors.brand.primary },
              isExporting && styles.exportButtonDisabled,
            ]}
            onPress={() => { void handleExportData(); }}
            disabled={isExporting}
            accessibilityRole="button"
            accessibilityLabel="Export data as CSV"
          >
            {isExporting ? (
              <ActivityIndicator size="small" color={isDark ? colors.brand.primary : '#1D4ED8'} style={styles.exportIcon} />
            ) : (
              <MaterialIcons name="file-download" size={16} color={isDark ? colors.brand.primary : '#1D4ED8'} style={styles.exportIcon} />
            )}
            <Text style={[styles.exportButtonText, isDark && { color: colors.brand.primary }]}>
              {isExporting ? 'Exporting...' : 'Export Data (CSV)'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      {/* <Text style={[styles.pageMeta, isDark && { color: colors.text.secondary }]}>NavigatorReports migration in progress: Overview is running on the new local adapter.</Text> */}


      <TicketingAnalyticsTabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange} />

      <TicketingAnalyticsFiltersPanel
        timePeriodTabs={TIME_PERIOD_TABS}
        timePeriod={timePeriod}
        onTimePeriodChange={handleTimePeriodChange}
        customDateRange={customDateRange}
        onCustomDateRangeChange={handleCustomDateRangeChange}
        options={filterOptionsWithAll}
        selected={selectedFilters}
        openFilterKey={openFilterKey}
        onOpenFilterKeyChange={setOpenFilterKey}
        onSelectFilter={handleSetSelectedFilter}
        onReset={handleReset}
        onApply={handleApply}
        hasChanges={canApplyFilters}
        summary={showSummary ? <TicketingAnalyticsSummaryRow text={summary.text} /> : null}
      />

      <View style={styles.contentScroll}>
        {activeTab === 'overview' ? (
          <Overview
            coreMetrics={overviewCoreMetrics}
            statusBreakdown={overviewStatusBreakdown}
            agingTickets={overviewAgingTickets}
            categoryBreakdown={overviewCategoryBreakdown}
            duplicateActivity={overviewDuplicateActivity}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'volume-trends' ? (
          <VolumeAndTrends
            weeklySection={weeklySection}
            ticketsByPrioritySection={ticketsByPrioritySection}
            resolutionByPrioritySection={resolutionByPrioritySection}
            submissionHeatMapSection={submissionHeatMapSection}
            resolutionHeatMapSection={resolutionHeatMapSection}
            priorityView={priorityView}
            onPriorityViewChange={setPriorityView}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'lifecycle' ? (
          <Lifecycle
            overviewSection={lifecycleOverviewSection}
            resolvingPartySection={lifecycleResolvingPartySection}
            staleTicketsSection={lifecycleStaleTicketsSection}
            resolutionTimePerCategorySection={lifecycleResolutionTimePerCategorySection}
            statusView={lifecycleSelectedCategories}
            onStatusViewChange={setLifecycleSelectedCategories}
            statusViewOptions={lifecycleCategoryOptions}
            statusByCategoryLoading={statusByCategoryLoading}
            statusByCategoryError={statusByCategoryError}
            statusByCategoryRows={statusByCategoryRows}
            categoryLookup={categoryLookup}
            onRetryStatusByCategory={retryStatusByCategory}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'team-workload' ? (
          <TeamWorkload
            loading={teamWorkloadLoading}
            error={teamWorkloadError}
            data={teamWorkloadData}
            onRetry={retryTeamWorkload}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'categories' ? (
          <Categories
            loading={categoriesLoading}
            error={categoriesError}
            data={categoriesData}
            breakdownView={categoriesBreakdownView}
            onBreakdownViewChange={setCategoriesBreakdownView}
            selectedCategory={categoriesSelectedCategory}
            onCategorySelect={setCategoriesSelectedCategory}
            topSubView={categoriesTopSubView}
            onTopSubViewChange={setCategoriesTopSubView}
            selectedCategoryFilters={appliedFilters.categories}
            rcaData={rcaData}
            onRetry={retryCategories}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'submitters-locations' ? (
          <SubmittersLocations
            loading={submittersLocationsLoading}
            error={submittersLocationsError}
            data={submittersLocationsData}
            onRetry={retrySubmittersLocations}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab === 'rca' ? (
          <Rca
            loading={rcaLoading}
            error={rcaError}
            data={rcaData}
            categoriesData={categoriesData}
            onRetry={retryRca}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        {activeTab !== 'overview' && activeTab !== 'volume-trends' && activeTab !== 'lifecycle' && activeTab !== 'team-workload' && activeTab !== 'categories' && activeTab !== 'submitters-locations' && activeTab !== 'rca' ? (
          <TicketingAnalyticsPlaceholderTab
            icon={placeholderCopy[activeTab as Exclude<TicketingAnalyticsTab, 'overview'>].icon}
            title={placeholderCopy[activeTab as Exclude<TicketingAnalyticsTab, 'overview'>].title}
            description={placeholderCopy[activeTab as Exclude<TicketingAnalyticsTab, 'overview'>].description}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  contentScroll: {
    flex: 1,
  },
  pageMeta: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#667085',
    marginTop: 6,
    marginBottom: 12,
  },
  // ─── Page title row with export button ───────────────────────────────────
  pageTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  pageTitleLeftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  pageIcon: {
    width: 40,
    height: 40,
  },
  pageTitleFlex: {
    flex: 1,
    marginBottom: 0,
  },
  pageTitleRightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: '#1D4ED8',
    backgroundColor: '#FFFFFF',
    marginLeft: 12,
  },
  exportButtonDisabled: {
    opacity: 0.6,
  },
  exportIcon: {
    marginRight: 5,
  },
  exportButtonText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#1D4ED8',
    fontWeight: '600',
  },
});

export default TicketingAnalyticsDashboard;






